'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:synergiefrontends":true});}catch(e){}}();

import React, { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { groupBy } from 'lodash';
import { Autocomplete, GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { Col, Container, Input, Row } from 'reactstrap';
import AgencyMapMarker from 'shared/components/AgencyMapMarker';
import AgencyNotFound from 'shared/components/svg/AgencyNotFound';
import useWindowWidth from 'shared/hooks/useWindowWidth';
import { mapCenter, mapZoom } from 'shared/utils/mapPositioning';
import { Locale } from 'shared/i18n/i18n-config';
import { getI18nUtils } from 'shared/i18n/i18n-utils';
import { Agency } from 'shared/pages/Agencies';
import Section from '@/components/shared/sections/Section';
import Button from '@/components/shared/buttons/Button';
import AgencyTeaser from '@/components/shared/AgencyTeaser';

// Copy from shared component -> Too many changes

interface SearchType {
  place: string;
  lat: string;
  lng: string;
}

type Props = {
  agencies: Agency[];
  dictionary: any;
  locale: Locale;
  query: { place: string; lat: string; lng: string; radius: string };
};

export default function Agencies({ agencies, dictionary, locale, query }: Props) {
  const { formatMessage } = getI18nUtils(dictionary);
  const router = useRouter();
  const width = useWindowWidth();
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: 'AIzaSyBupFH0E8sisWkj87o6MEYxFRueNVm9RuY',
    libraries: ['places'],
  });
  const [autoComplete, setAutoComplete] = useState<google.maps.places.Autocomplete | null>(null);
  const [agencySelected, setAgencySelected] = useState<string | null>(null);
  const [search, setSearch] = useState<SearchType | null>(null);
  const defaultRadius = '25000';
  const [radius, setRadius] = useState<string>(defaultRadius);
  const onMobile = width && width < 768;
  const center = mapCenter(agencies);
  const zoom = onMobile ? mapZoom(agencies) - 1 : mapZoom(agencies);
  const groupedAgencies = groupBy(agencies, (item) => `${item.lat}+${item.lng}`);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { place, lat, lng, radius: queryRadius } = query;

  useEffect(() => {
    if (inputRef.current) inputRef.current.value = '';
    setRadius(defaultRadius);
    setSearch(null);
    setAgencySelected(null);
  }, [locale]);

  useEffect(() => {
    if (isLoaded) inputRef.current = document.getElementById('autocomplete') as HTMLInputElement;
    if (isLoaded && place && lat && lng && queryRadius && inputRef?.current) {
      setSearch({ place, lat, lng });
      setRadius(queryRadius);
      inputRef.current.value = query.place;
    }
  }, [isLoaded]);

  const onPlaceChanged = () => {
    if (autoComplete === null) return;
    const addressObject = autoComplete.getPlace();
    if (addressObject.geometry && inputRef?.current) {
      const { lat: addressObjectLat, lng: addressObjectLng } = addressObject.geometry.location as google.maps.LatLng;
      const newPlace = inputRef.current.value;
      const newRadius = search ? defaultRadius : radius;
      setSearch({
        place: newPlace,
        lat: addressObjectLat().toString(),
        lng: addressObjectLng().toString(),
      });
      setRadius(newRadius);
      const queryParams = {
        place: newPlace,
        lat: String(addressObjectLat()),
        lng: String(addressObjectLng()),
        radius: newRadius,
      };
      const queryParamString = new URLSearchParams(queryParams).toString();
      router.push(`/${locale}/contact?${queryParamString}`);
    }
  };

  const onRadiusChanged = (newRadius: string) => {
    setRadius(newRadius);
    const queryParams = {
      ...(search?.place && { place: search.place }),
      ...(search?.lat && { lat: search.lat }),
      ...(search?.lng && { lng: search.lng }),
      radius: newRadius,
    };
    const queryParamString = new URLSearchParams(queryParams).toString();
    if (search) router.push(`/${locale}/contact?${queryParamString}`);
  };

  const reset = () => {
    setSearch(null);
    setAgencySelected(null);
    if (inputRef?.current) inputRef.current.value = '';
    router.push(`/${locale}/contact/`);
  };

  return (
    <>
      {/* Upper part */}
      <div className="upper-part pb-5 pb-lg-0">
        <Container>
          <Row className="d-flex justify-content-end align-items-center py-5">
            <Col xs="12" lg="5">
              <h1 className="">{formatMessage({ id: 'agencies-h1' })}</h1>
            </Col>

            {/* PLACE */}
            <Col xs="8" lg="5" className="pe-0">
              {isLoaded ? (
                <Autocomplete
                  onPlaceChanged={onPlaceChanged}
                  onLoad={(c) => setAutoComplete(c)}
                  types={['(regions)']}
                  restrictions={{ country: 'be' }}
                >
                  <div className="position-relative">
                    <Input
                      type="text"
                      placeholder={formatMessage({
                        id: 'agencies-search-placeholder',
                      })}
                      aria-label="search agencies"
                      id="autocomplete"
                      autoComplete="off"
                      className="place-search"
                    />
                    {/* Reset button */}
                    {search && (
                      <button onClick={reset} className="position-absolute top-0 end-0 bg-white my-2 border-0 mx-3">
                        <span className="text-blue">&#10005;</span>
                      </button>
                    )}
                  </div>
                </Autocomplete>
              ) : (
                <div className="place-search w-100 ph" style={{ height: '38px' }} />
              )}
            </Col>
            {/* RADIUS */}
            <Col xs={4} lg="2" className="ps-0">
              <Input
                type="select"
                name="radius"
                id="filterSelect"
                value={radius}
                onChange={(e) => onRadiusChanged(e.target.value)}
                className="radius-select"
              >
                <option value="10000">10 km</option>
                <option value="25000">25 km</option>
                <option value="50000">50 km</option>
                <option value="75000">75 km</option>
                <option value="100000">100 km</option>
              </Input>
            </Col>
          </Row>
          {/* Map */}
          {isLoaded ? (
            <GoogleMap
              mapContainerClassName="map-container"
              center={center}
              zoom={zoom}
              options={{ disableDefaultUI: true, zoomControl: true }}
              onClick={() => setAgencySelected(null)}
            >
              {Object.keys(groupedAgencies).map((agencyGroup) => (
                <AgencyMapMarker
                  dictionary={dictionary}
                  key={groupedAgencies[agencyGroup][0].id}
                  agencyGroup={groupedAgencies[agencyGroup]}
                  agencySelected={agencySelected}
                  setAgencySelected={setAgencySelected}
                  prependBrandName
                />
              ))}
            </GoogleMap>
          ) : (
            <div className="map-container ph" />
          )}
        </Container>
      </div>
      {/* Cards */}
      <Section type="gray" className="py-5">
        {agencies.length === 0 && search && (
          <Row>
            <Col
              xs={{ size: 12, order: 2 }}
              lg={{ size: 6, order: 1 }}
              className="d-flex justify-content-center align-items-center"
            >
              <div>
                <h2>{formatMessage({ id: 'pagenav-agencies' })}</h2>
                <p>{formatMessage({ id: 'agencies_not_found' }, { place: search.place, radius: Number(radius) / 1000 })}</p>
                <Button onClick={reset} type="outline">
                  {formatMessage({ id: 'clear_all_filters' })}
                </Button>
              </div>
            </Col>
            <Col
              xs={{ size: 12, order: 1 }}
              lg={{ size: 6, order: 2 }}
              className="d-flex justify-content-center align-items-center"
            >
              <AgencyNotFound />
            </Col>
          </Row>
        )}
        <Row>
          {agencies.map((agency) => (
            <Col md={6} lg={4} key={agency.id} className="pb-4">
              <AgencyTeaser agency={agency} dictionary={dictionary} locale={locale} className="pt-5" prependBrandName />
            </Col>
          ))}
        </Row>
      </Section>
    </>
  );
}
